import type { AccountType, CreateAccountRequest } from '@fleece/models';
import { assertAccountId, assertAccountName } from '@fleece/utilities';

/** The create-account form as typed. */
export interface AccountDraft {
  readonly name: string;
  /** Empty to have the service generate one. */
  readonly accountId: string;
  readonly accountType: AccountType;
}

export function newAccountDraft(): AccountDraft {
  // Paper, so the form's first answer is the one that cannot cost money.
  return { name: '', accountId: '', accountType: 'paper' };
}

/**
 * What stops the draft being created, in the service's own words, or undefined when it can
 * be. The same assertions the service runs, so the form never accepts what the request would
 * then refuse, nor refuses what it would accept.
 */
export function accountDraftProblem(draft: AccountDraft): string | undefined {
  try {
    assertAccountName(draft.name.trim());
    if (draft.accountId !== '') {
      assertAccountId(draft.accountId);
    }
    return undefined;
  } catch (error) {
    return error instanceof Error ? error.message : String(error);
  }
}

export function accountCreateRequest(draft: AccountDraft): CreateAccountRequest {
  const name = draft.name.trim();
  return draft.accountId === '' ? { name, accountType: draft.accountType } : { accountId: draft.accountId, name, accountType: draft.accountType };
}

/**
 * An id as it is typed: upper-cased, since ids are uppercase only and a lowercase one is
 * the same id mistyped rather than a different one, and stripped of spaces.
 */
export function normaliseAccountIdInput(typed: string): string {
  return typed.toUpperCase().replace(/\s+/g, '');
}

/** What each account type means, for the form to say beside the choice. */
export const ACCOUNT_TYPE_HINTS: Readonly<Record<AccountType, string>> = {
  paper: "Trades the broker's paper account. Nothing real moves.",
  live: 'Trades real money in the live broker account.',
  mirror: 'Kept in step by hand, for a broker with no API.',
};
